import { useEffect, useRef, useState } from "react";
import { EditorialImage } from "./EditorialImage";
import { cn } from "@/lib/utils";
import { AVIF_TYPE, WEBP_TYPE, avifFor, dimsFor, srcSetFor } from "@/lib/image-formats";

import type { ReactNode } from "react";

/**
 * Hero éditorial pleine largeur pour les pages de collection.
 * Reçoit une image, un eyebrow, un titre et un sous-titre centrés en bas du bloc.
 */
export function CategoryHero({
  image,
  mobileImage,
  imageAlt,
  eyebrow,
  title,
  subtitle,
  imagePosition,
  mobileImagePosition,
  children,
  className,
}: {
  image?: string;
  mobileImage?: string;
  imageAlt: string;
  eyebrow?: string;
  title: ReactNode;
  subtitle?: string;
  imagePosition?: string;
  mobileImagePosition?: string;
  children?: ReactNode;
  className?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imgRef.current?.complete) setLoaded(true);
  }, [image, mobileImage]);

  return (
    <section className={cn("relative w-full overflow-hidden", className)}>
      <div className="relative h-[55vh] min-h-[420px] w-full overflow-hidden bg-sand sm:h-[62vh] sm:min-h-[480px] lg:h-[68vh] lg:max-h-[720px]">
        {/* Placeholder de hauteur réservée : aucun saut avant le décodage de la photo */}
        {!loaded ? (
          <div aria-hidden="true" className="skeleton-shimmer absolute inset-0 h-full w-full" />
        ) : null}
        <picture className="absolute inset-0 h-full w-full">
          {mobileImage && avifFor(mobileImage) ? (
            <source
              media="(max-width: 639px)"
              type={AVIF_TYPE}
              srcSet={srcSetFor(mobileImage, "avif") ?? avifFor(mobileImage)}
              sizes="100vw"
            />
          ) : null}
          {mobileImage ? (
            <source
              media="(max-width: 639px)"
              type={WEBP_TYPE}
              srcSet={srcSetFor(mobileImage, "webp") ?? mobileImage}
              sizes="100vw"
            />
          ) : null}
          {avifFor(image) ? (
            <source
              type={AVIF_TYPE}
              srcSet={srcSetFor(image, "avif") ?? avifFor(image)}
              sizes="100vw"
            />
          ) : null}
          {srcSetFor(image, "webp") ? (
            <source type={WEBP_TYPE} srcSet={srcSetFor(image, "webp")} sizes="100vw" />
          ) : null}

          <img
            ref={imgRef}
            src={image}
            alt={imageAlt}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            sizes="100vw"
            {...(dimsFor(image) ?? {})}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            className={cn(
              "absolute inset-0 h-full w-full object-cover transition-opacity duration-500",
              loaded ? "opacity-100" : "opacity-0",
              mobileImagePosition,
              imagePosition,
            )}
          />
        </picture>


        <div
          className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-gradient-to-b from-foreground/15 via-transparent to-transparent"
          aria-hidden="true"
        />

        <div className="absolute inset-x-0 bottom-0 mx-auto flex max-w-7xl flex-col items-center px-6 pb-14 text-center sm:pb-20 lg:pb-28">
          {eyebrow ? <p className="label-xs text-accent">{eyebrow}</p> : null}
          <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-[0.01em] text-foreground sm:mt-5 sm:text-5xl md:text-6xl lg:text-7xl">
            {title}
          </h1>
          {subtitle ? (
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:mt-5 sm:text-base">
              {subtitle}
            </p>
          ) : null}
          {children ? <div className="mt-8 sm:mt-10">{children}</div> : null}
        </div>
      </div>
    </section>
  );
}
